// Imports ------------------------------------------------------------------->
import { CustomElement } from '../CustomElement'
import { element } from '../CustomElement'
import { Rect } from '../../utils/Rect'
import { InputSampler } from './InputSampler'
import { getWebglContext } from '../../utils/webgl'
import * as array from '../../utils/array'

import { Link } from '../flipp-link/Link'
import { LazyImage } from '../flipp-lazy-image/LazyImage'
import { FlyerView } from './FlyerView'

import { inject } from '../../services/di'
import { SeoService } from '../../services/SeoService'
import { FlyerService } from '../../services/FlyerService'
import { ClippingService } from '../../services/ClippingService'
import { CouponService } from '../../services/CouponService'
import { AuthenticationService } from '../../services/AuthenticationService'
import { AuthStatus } from '../../services/definitions/IAuthenticationService'
import { LoyaltyProgramService } from '../../services/LoyaltyProgramService'
import {
    ShoppingListService,
    SLFlyerItemClipping,
} from '../../services/ShoppingListService'
import { PGPrintCouponService } from '../../services/PGPrintCouponService'

import {
    Flyer,
    FlyerDetails,
    FlyerItem,
} from '../../definitions/FlyerDefinitions'
import {
    FlyerItemCoupon,
    ClippedCoupon,
} from '../../definitions/CouponDefinitions'
import { DisplayType } from '../../definitions/BackflippDefinitions'
import { LoyaltyCard, ClippingType } from '../../definitions/CommonDefinitions'

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

export function register() {
    element('flipp-flyerview')(FlyerViewElement)
}

export class FlyerViewElement extends CustomElement {
    @inject seo: SeoService
    @inject clippings: ClippingService
    @inject flyerService: FlyerService
    @inject couponService: CouponService
    @inject auth: AuthenticationService
    @inject loyaltyProgramService: LoyaltyProgramService
    @inject shoppingList: ShoppingListService
    @inject pgPrintService: PGPrintCouponService

    initialBounds: Rect
    onboundschange: (controller: FlyerViewElement) => void
    oninteraction: (controller: FlyerViewElement) => void

    private _canvas: HTMLCanvasElement
    private _linksContainer: HTMLElement
    private _inputSampler: InputSampler

    private _flyerView: FlyerView
    private _gl: WebGLRenderingContext
    private _linksWithImages = false

    private _flyer: Flyer
    private _items: FlyerItem[]
    private _pages: Rect[]
    private _coupons: FlyerItemCoupon[]
    private _details: FlyerDetails
    private _pageItems: FlyerItem[][]
    private _clippedCouponIds: number[]

    private _ignoreNextFocus = 0
    private _forceZoomNextFocus = false
    private _attaching = false
    private _animationRequest: number
    private _links: { [id: string]: HTMLAnchorElement } = {}

    constructor() {
        super()
        this.style.cursor = 'default'
        this.style.display = 'block'

        this._canvas = document.createElement('canvas')
        this._canvas.setAttribute('fill-absolute', '')
        this._canvas.style.cursor = 'inherit'
        this._canvas.style.outline = 'none'
        this._canvas.style.display = 'block'
        this.appendChild(this._canvas)

        this._linksContainer = document.createElement('div')
        this._canvas.appendChild(this._linksContainer)

        this._reload()
    }

    get attaching() {
        return this._attaching
    }

    handleResize() {
        return this._handleZoom()
    }

    get itemId(): number {
        return this.hasAttribute('itemid')
            ? parseInt(this.getAttribute('itemid'), 10)
            : null
    }

    set itemId(value: number) {
        this.setAttribute('itemid', value.toString())
    }

    get flyerId(): number {
        return this.hasAttribute('flyerid')
            ? parseInt(this.getAttribute('flyerid'), 10)
            : null
    }

    set flyerId(value: number) {
        this.setAttribute('flyerid', value.toString())
    }

    get showMatchups() {
        return this.hasAttribute('showmatchups')
            ? this.getAttribute('showmatchups') === 'true'
            : true
    }

    get isFastFlyer() {
        return this.hasAttribute('fastflyer')
            ? this.getAttribute('fastflyer') === 'true'
            : false
    }

    scaleDown() {
        if (this._flyerView) this._flyerView.scaleDown()
    }

    scaleUp() {
        if (this._flyerView) this._flyerView.scaleUp()
    }

    pageLeft() {
        if (!this._flyerView) return

        const controller = this._flyerView.controller
        const currentPage = this._currentPage()
        if (currentPage - 1 === -1) {
            if (controller.scrollLeft > 0) {
                // zoom to the start
                controller.zoomToRect(
                    0,
                    0,
                    controller.viewportWidth / controller.scale,
                    controller.viewportHeight / controller.scale,
                    true
                )
            }
            return
        }

        const rect = this._pages[currentPage - 1]
        controller.zoomToRect(
            rect.left +
                rect.width / 2 -
                controller.viewportWidth / controller.scale / 2,
            0,
            controller.viewportWidth / controller.scale,
            controller.viewportHeight / controller.scale,
            true
        )
    }

    pageRight() {
        if (!this._flyerView) return

        const controller = this._flyerView.controller
        const currentPage = this._currentPage()
        if (currentPage + 1 === this._pages.length) {
            const visibleWidth = controller.viewportWidth / controller.scale
            if (controller.scrollLeft + visibleWidth < controller.scrollWidth) {
                // zoom to the end
                controller.zoomToRect(
                    controller.scrollWidth - visibleWidth,
                    0,
                    controller.viewportWidth / controller.scale,
                    controller.viewportHeight / controller.scale,
                    true
                )
            }

            return
        }

        const rect = this._pages[currentPage + 1]
        controller.zoomToRect(
            rect.left +
                rect.width / 2 -
                controller.viewportWidth / controller.scale / 2,
            0,
            controller.viewportWidth / controller.scale,
            controller.viewportHeight / controller.scale,
            true
        )
    }

    goToPage(pageNumber: number) {
        const rect = this._pages[pageNumber]
        const controller = this._flyerView.controller
        controller.zoomToRect(
            rect.left +
                rect.width / 2 -
                controller.viewportWidth / controller.scale / 2,
            0,
            controller.viewportWidth / controller.scale,
            controller.viewportHeight / controller.scale,
            true
        )
    }

    get controller() {
        return this._flyerView ? this._flyerView.controller : null
    }

    protected attachedCallback() {
        this._attaching = true

        window.addEventListener('flipp-clippingschanged', this._redrawClippings)
        window.addEventListener('flipp-auth', this._redrawClippings)
        window.addEventListener('flipp-loyaltycards', this._redrawClippings)
        window.addEventListener('flipp-couponclipped', this._redrawClippings)
        window.addEventListener('flipp-shopping-list', this._redrawClippings)
        window.addEventListener(
            'flipp-devicepixelratiochange',
            this._handleZoom
        )
        if (!this._inputSampler) {
            this._inputSampler = new InputSampler(
                this._canvas,
                this.parentElement
            )
            this._inputSampler.oninput = this._handleInput
            this._inputSampler.ontrackinginput = this._handleInput
        }
        this._inputSampler.attach()
        this._canvas.addEventListener('focus', this._handleItemFocus, true)
        this._canvas.addEventListener('blur', this._handleItemBlur, true)
        this._canvas.addEventListener(
            'webglcontextlost',
            this._handleContextLost
        )
        this._canvas.addEventListener(
            'webglcontextrestored',
            this._handleContextRestored
        )

        this.ownerDocument.defaultView.addEventListener(
            'resize',
            this._handleResize
        )

        if (this._animationRequest === undefined)
            this._animationRequest = window.requestAnimationFrame(
                this._handleRequestAnimationFrame
            )

        if (!this._gl) this._handleContextRestored()

        this.handleResize()
        if (this.itemId) {
            const itemLink = this.querySelector(
                `a[itemid='${this.itemId}']`
            ) as HTMLAnchorElement
            if (itemLink) {
                this._forceZoomNextFocus = true
                itemLink.focus()
            }
        }

        this._attaching = false
    }

    protected detachedCallback() {
        window.removeEventListener(
            'flipp-clippingschanged',
            this._redrawClippings
        )
        window.removeEventListener('flipp-auth', this._redrawClippings)
        window.removeEventListener('flipp-loyaltycards', this._redrawClippings)
        window.removeEventListener('flipp-couponclipped', this._redrawClippings)
        window.removeEventListener('flipp-shopping-list', this._redrawClippings)
        window.removeEventListener(
            'flipp-devicepixelratiochange',
            this._handleZoom
        )
        this._inputSampler.detach()
        this._canvas.removeEventListener('focus', this._handleItemFocus, true)
        this._canvas.removeEventListener('blur', this._handleItemBlur, true)
        this._canvas.removeEventListener(
            'webglcontextlost',
            this._handleContextLost
        )
        this._canvas.removeEventListener(
            'webglcontextrestored',
            this._handleContextRestored
        )

        this.ownerDocument.defaultView.removeEventListener(
            'resize',
            this._handleResize
        )
    }

    protected attributeChangedCallback(
        attr: string,
        oldValue: any,
        newValue: any
    ) {
        if (
            attr === 'flyerid' ||
            (attr === 'fastflyer' && newValue === 'true')
        ) {
            this._reload()
        }
    }

    private _handleInput = () => {
        if (this._animationRequest === undefined)
            this._animationRequest = window.requestAnimationFrame(
                this._handleRequestAnimationFrame
            )
    }

    private _redrawClippings = () => {
        Promise.all([
            this.flyerService.getFlyer(this.flyerId),
            this.flyerService.getFlyerDetails(this.flyerId),
            this.couponService.getCouponsForFlyer(this.flyerId),
            this._getClippedCouponIds(),
        ]).then(([flyer, details, coupons, clippedCouponIds]) => {
            this._flyer = flyer
            this._clippedCouponIds = clippedCouponIds
            this._flyerView.setClippings(
                this._getItemClippings(coupons, details, flyer)
            )
            this._flyerView.setClippedCoupons(this._clippedCouponIds)
        })
    }

    private _handleInvalidate = () => {
        if (this._animationRequest === undefined)
            this._animationRequest = window.requestAnimationFrame(
                this._handleRequestAnimationFrame
            )
    }

    private _handleRequestAnimationFrame = () => {
        if (!this._flyerView) return

        if (this._flyerView.animating) this._flyerView.scene.invalidate(false)

        const sample = this._inputSampler.poll()
        if (sample) this._flyerView.input(sample)

        this._flyerView.scene.draw()

        this._animationRequest = undefined
        if (
            (this._flyerView.animating &&
                this._animationRequest === undefined) ||
            this._inputSampler.polling
        )
            this._animationRequest = window.requestAnimationFrame(
                this._handleRequestAnimationFrame
            )
    }

    private _populateLinks() {
        while (this._linksContainer.hasChildNodes())
            this._linksContainer.removeChild(this._linksContainer.firstChild)

        let focusedLink: HTMLAnchorElement = null
        for (const pageItems of this._pageItems) {
            for (const item of pageItems) {
                const a = (
                    <Link
                        class="item-container"
                        to={item}
                        aria-label={item.name}
                    >
                        {this._linksWithImages ? (
                            <LazyImage
                                fit
                                contain
                                dataSrc={item.cutoutImageUrl}
                                alt=""
                            />
                        ) : null}
                        <div>{item.name}</div>
                    </Link>
                ) as HTMLAnchorElement

                a.setAttribute('itemid', item.id.toString())
                a.addEventListener('click', this._handleItemClick)
                this._links[item.id] = a
                this._linksContainer.appendChild(a)

                if (item.id === this.itemId) focusedLink = a
            }
        }

        this._handleResize()
        if (focusedLink) {
            this._forceZoomNextFocus = true
            this._focusItem(focusedLink, true)
        } else {
            const initialBounds = this.initialBounds
            if (this._flyerView && initialBounds) {
                this._flyerView.controller.zoomToRect(
                    initialBounds.left,
                    initialBounds.top,
                    initialBounds.width,
                    initialBounds.height,
                    false
                )
            }
        }
    }

    private _handleContextRestored = () => {
        const contextOptions = { stencil: true, alpha: false }
        const gl = getWebglContext(this._canvas, contextOptions)

        if (gl) {
            this._gl = gl
        } else {
            this._linksWithImages = true
            if (this._canvas.parentNode) {
                this._canvas.parentNode.removeChild(this._canvas)
            }

            this.appendChild(this._linksContainer)
            document.documentElement.setAttribute('nocanvas', '')

            if (this._items) this._populateLinks()
            return
        }

        this._flyerView = new FlyerView(this._gl)
        this._flyerView.onboundschange = () => {
            if (this.onboundschange) this.onboundschange(this)
        }
        this._flyerView.oninteraction = () => {
            if (this.oninteraction) this.oninteraction(this)
        }
        this._flyerView.onbegindrag = () => {
            this.style.cursor = 'move'
            this.style.cursor = '-ms-grabbing'
            this.style.cursor = '-moz-grabbing'
            this.style.cursor = '-webkit-grabbing'
        }
        this._flyerView.onenddrag = () => {
            this.style.cursor = 'auto'
        }
        this._flyerView.onitemhover = (id: number) => {
            this.style.cursor = id ? 'pointer' : 'auto'
        }
        this._flyerView.onitempop = (id: number) => {
            const link = this._links[id]
            if (link) {
                this._ignoreNextFocus = 2
                link.focus()
            }

            this.dispatchEvent(
                new CustomEvent('itempop', {
                    cancelable: true,
                    bubbles: true,
                    detail: id,
                })
            )
        }
        if (this._flyer && this._items)
            this._flyerView.init(
                this._flyer,
                this._items,
                this.showMatchups ? this._coupons : [],
                this._clippedCouponIds,
                this._getItemClippings(
                    this._coupons,
                    this._details,
                    this._flyer
                )
            )
        this._flyerView.scene.oninvalidate = this._handleInvalidate

        this._handleResize()
        setTimeout(this._handleResize, 0)

        // Clear the background to canvas doesn't get reset to black. This seems to happen on certain
        // browsers (Chrome, Safari) when the canvas is resized.
        // See: FW-1029
        gl.clearColor(0.894, 0.894, 0.894, 1)
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)

        const initialBounds = this.initialBounds
        if (initialBounds) {
            this._flyerView.controller.zoomToRect(
                initialBounds.left,
                initialBounds.top,
                initialBounds.width,
                initialBounds.height,
                false
            )
        }
    }

    private _handleItemClick = (e: Event) => {
        e.preventDefault()
        const el = e.target
        if (el instanceof HTMLAnchorElement) {
            const id = el.getAttribute('itemid')
            if (id) {
                this.dispatchEvent(
                    new CustomEvent('itempop', {
                        cancelable: true,
                        bubbles: true,
                        detail: id,
                    })
                )
            }
        }
    }

    private _handleItemBlur = () => {
        if (this._flyerView) this._flyerView.focus(null, false, false)
    }

    private _focusItem = (item: HTMLAnchorElement, zoom: boolean) => {
        const itemId = item.getAttribute('itemid')
        if (itemId) {
            const item = array.find(
                this._items,
                (i) => i.id.toString() === itemId
            )
            if (item && this._flyerView && this._ignoreNextFocus === 0) {
                this._flyerView.focus(item, zoom, this._forceZoomNextFocus)
            }
        }
    }

    private _handleItemFocus = (e: FocusEvent) => {
        const item = e.target
        if (item instanceof HTMLAnchorElement) {
            this._focusItem(item, e.relatedTarget !== null)
        }

        this._forceZoomNextFocus = false
        this._ignoreNextFocus = Math.max(0, this._ignoreNextFocus - 1)
    }

    private _handleContextLost = (e: Event) => {
        e.preventDefault()
        this._gl = null
    }

    private _getClippedCouponIds = () => {
        const flyerId = this.flyerId
        if (!flyerId) return Promise.resolve<number[]>([])

        let clippedCouponsFn = Promise.resolve<ClippedCoupon[]>([])
        let loyaltyCardsFn = Promise.resolve<LoyaltyCard[]>([])
        if (this.auth.status === AuthStatus.LOGGED_IN) {
            clippedCouponsFn = this.couponService.getClippedCoupons(
                this.auth.user
            )
            loyaltyCardsFn = this.loyaltyProgramService.getLoyaltyCards(
                this.auth.user
            )
        }
        return Promise.all([
            this.flyerService.getFlyer(flyerId),
            this.loyaltyProgramService.getLoyaltyProgramMerchants(),
            clippedCouponsFn,
            loyaltyCardsFn,
        ]).then(([flyer, programMerchants, clippedCoupons, cards]) => {
            let clippedCouponIds: number[] = []
            /* Filter clipped coupons to only those that can actually be available on this flyer (eg: for a coupon that can
             * be clipped to multiple vendors, make sure it's actually clipped to this merchant before displaying it as clipped */
            const programMerchant = array.find(
                programMerchants,
                (entry) => entry.merchantId === flyer.merchantId
            )
            if (flyer.displayType === 'cpg_book') {
                const validLoyaltyProgramIds = cards.map(
                    (card) => card.loyaltyProgramId
                )
                clippedCouponIds = clippedCoupons
                    .filter(
                        (coupon) =>
                            validLoyaltyProgramIds.indexOf(
                                coupon.program.loyaltyProgramId
                            ) >= 0
                    )
                    .map((coupon) => coupon.program.couponId)
            } else if (programMerchant) {
                const cardProgramIds = cards.map(
                    (card) => card.loyaltyProgramId
                )
                clippedCouponIds = clippedCoupons
                    .filter(
                        (coupon) =>
                            coupon.program.loyaltyProgramId ===
                                programMerchant.loyaltyProgramId &&
                            cardProgramIds.indexOf(
                                coupon.program.loyaltyProgramId
                            ) !== -1
                    )
                    .map((coupon) => coupon.program.couponId)
            } else {
                clippedCouponIds = []
            }

            /* Print coupons can be 'clipped', but those are saved in localStorage instead of postcard
             * Merge the clipped print coupon IDs into the array of L2C coupons */
            const clippings = this.clippings.getClippings()
            clippings.forEach((clipping) => {
                if (clipping.type === ClippingType.PRINT_COUPON) {
                    clippedCouponIds.push(Number(clipping.item.id))
                }
            })

            /* Merge in coupons from the P&G Print Coupon service. This is terrible but it's the easiest way for the
             * FlyerView to get access to print coupons in the P&G Experience. In the default (flipp.com) experience,
             * this should have no effect.
             * This will be removed when we refactor the print coupon support (remove ClippingService, PGPrintCouponService)
             * and move everything into the CouponService. */
            clippedCouponIds = clippedCouponIds.concat(
                this.pgPrintService
                    .getCoupons()
                    .map((coupon) => Number(coupon.couponId))
            )
            return clippedCouponIds
        })
    }

    private _reload() {
        const flyerId = this.flyerId
        if (!flyerId) {
            return
        }

        const p1 = this.flyerService.getFlyer(flyerId)
        const p2 = this.flyerService.getFlyerDetails(flyerId)
        const p3 = this.couponService.getCouponsForFlyer(flyerId)
        const p4 = this._getClippedCouponIds()

        Promise.all([p1, p2, p3, p4]).then((results) => {
            const [flyer, details, coupons, clippedCouponIds] = results

            this._flyer = flyer
            this._items = details.items
                .filter(
                    (item) => item.displayType !== DisplayType.ONLYFEATUREHTML
                )
                .sort((lhs, rhs) => (lhs.left < rhs.left ? -1 : 1))

            this._coupons = coupons
            this._details = details
            this._clippedCouponIds = clippedCouponIds

            this._pages = details.pages
                .map(
                    (p) =>
                        new Rect(
                            p.left,
                            -p.top,
                            p.right - p.left,
                            p.top - p.bottom
                        )
                )
                .sort((lhs, rhs) => (lhs.left <= rhs.left ? -1 : 1))

            this._pageItems = []
            for (let i = 0; i < this._pages.length; ++i) {
                const page = this._pages[i]
                const pageItems: FlyerItem[] = []
                this._pageItems.push(pageItems)
                for (let j = 0; j < this._items.length; ++j) {
                    const item = this._items[j]
                    const itemRect = new Rect(
                        item.left,
                        -item.top,
                        item.right - item.left,
                        item.top - item.bottom
                    )
                    if (Rect.intersects(itemRect, page)) pageItems.push(item)
                }
                pageItems.sort((lhs, rhs) => {
                    if (Math.abs(lhs.top - rhs.top) > 50)
                        return -lhs.top <= -rhs.top ? -1 : 1
                    else return lhs.left <= rhs.left ? -1 : 1
                })
            }

            if (this._flyerView)
                this._flyerView.init(
                    this._flyer,
                    this._items,
                    this.showMatchups ? this._coupons : [],
                    this._clippedCouponIds,
                    this._getItemClippings(coupons, details, flyer)
                )
            this._populateLinks()

            this._handleResize()
            setTimeout(this._handleResize, 0)
        })
    }

    private _getItemClippings = (
        coupons: FlyerItemCoupon[],
        details: FlyerDetails,
        flyer: Flyer
    ) => {
        /* Get a list of CPG V2/V3 coupons. These corresponding FlyerItems for these coupons cannot be clipped
         * directly, but we still want them to appear circled w/ a yellow coupon badge in the Flyer View */
        const cpgCouponIds = coupons
            .filter(
                (coupon) =>
                    this._clippedCouponIds.indexOf(coupon.couponId) !== -1 &&
                    (coupon.displayType === DisplayType.COUPONV2 ||
                        coupon.displayType === DisplayType.COUPONV3)
            )
            .map((coupon) => coupon.item.id)
        return details.items
            .filter((item) => cpgCouponIds.indexOf(item.id) !== -1)
            .map(
                (item) =>
                    new SLFlyerItemClipping({
                        flyer_id: flyer.id,
                        flyer_item_id: item.id,
                        name: item.name,
                        right: item.right,
                        left: item.left,
                        top: item.top,
                        bottom: item.bottom,
                        price: item.price,
                        valid_to: item.validTo,
                        thumbnail_url: item.cutoutImageUrl,
                        merchant_name: flyer.merchant,
                        merchant_logo_url: flyer.merchantLogo,
                    })
            )
            .concat(this.shoppingList.localList.flyerItemClippings)
    }

    private _handleZoom = () => {
        if (this.offsetWidth === 0 || this.offsetHeight === 0) return

        const canvasSizeLimit = 4096
        const canvasSizeRatio = flipp.devicePixelRatio
        let newCanvasWidth = Math.floor(this.offsetWidth * canvasSizeRatio)
        let newCanvasHeight = Math.floor(this.offsetHeight * canvasSizeRatio)
        if (newCanvasHeight > canvasSizeLimit) {
            newCanvasWidth = Math.floor(
                (canvasSizeLimit * newCanvasWidth) / newCanvasHeight
            )
            newCanvasHeight = canvasSizeLimit
        }
        if (newCanvasWidth > canvasSizeLimit) {
            newCanvasHeight = Math.floor(
                (canvasSizeLimit * newCanvasHeight) / newCanvasWidth
            )
            newCanvasWidth = canvasSizeLimit
        }

        this._canvas.style.width = this.offsetWidth + 'px'
        this._canvas.style.height = this.offsetHeight + 'px'
        this._canvas.width = newCanvasWidth
        this._canvas.height = newCanvasHeight

        if (!this._flyerView) return

        const scene = this._flyerView.scene
        const controller = this._flyerView.controller
        if (scene.camera) {
            scene.camera.viewportWidth = newCanvasWidth
            scene.camera.viewportHeight = newCanvasHeight
            scene.refreshViewport()
        }
        if (scene.debugCamera) {
            scene.debugCamera.viewportWidth = newCanvasWidth
            scene.debugCamera.viewportHeight = newCanvasHeight
        }
        if (controller && scene.camera) {
            controller.viewportWidth = newCanvasWidth
            controller.viewportHeight = newCanvasHeight

            const minimumScale =
                controller.viewportHeight / controller.scrollHeight
            controller.scale = minimumScale
            controller.minimumScale = minimumScale
            controller.maximumScale = flipp.devicePixelRatio
        }
        scene.draw()
    }

    private _handleResize = () => {
        this._handleZoom()
    }

    private _currentPage() {
        const controller = this._flyerView.controller
        const midX =
            controller.scrollLeft +
            controller.viewportWidth / controller.scale / 2
        const midY =
            controller.scrollTop +
            controller.viewportHeight / controller.scale / 2

        for (let i = 0, n = this._pages.length; i < n; ++i) {
            const rect = this._pages[i]
            if (rect.containsPoint(midX, midY)) return i
        }

        return 0
    }
}
